import PropTypes from "prop-types";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Button } from "shared/Button";
import EditIcon from "shared/assets/Edit_Icon.svg";
import TrashButtonIcon from "shared/assets/Trash_Btn.svg";
import PlaylistCoverArt from "../PlaylistCoverArt";
import styles from "./PlaylistThumbnailPreview.module.css";

function sanitizeThumbnails(thumbnails) {
  return thumbnails.filter(Boolean);
}

export function PlaylistThumbnailPreview({
  thumbnails,
  overflowCount,
  hasAudios,
  onAddExisting,
  onDrop,
  onUseAudios,
  onEdit,
  onDelete,
}) {
  const inputRef = useRef(null);
  const [isDragActive, setIsDragActive] = useState(false);
  const [showNoAudiosState, setShowNoAudiosState] = useState(false);

  const coverThumbnails = useMemo(
    () => sanitizeThumbnails(thumbnails),
    [thumbnails],
  );
  const hasCoverArt = coverThumbnails.length > 0;
  const isSingleImagePreview =
    coverThumbnails.length === 1 && overflowCount <= 0;

  useEffect(() => {
    if (hasCoverArt || hasAudios) {
      setShowNoAudiosState(false);
    }
  }, [hasCoverArt, hasAudios]);

  const handleAddExistingClick = () => {
    onAddExisting();
    inputRef.current?.click();
  };

  const handleInputChange = (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";

    if (!file || !file.type.startsWith("image/")) {
      return;
    }

    onAddExisting(file);
    setShowNoAudiosState(false);
  };

  const handleDragOver = (event) => {
    if (hasCoverArt) {
      return;
    }

    event.preventDefault();
    setIsDragActive(true);
  };

  const handleDragLeave = () => {
    setIsDragActive(false);
  };

  const handleDrop = (event) => {
    if (hasCoverArt) {
      return;
    }

    event.preventDefault();
    setIsDragActive(false);

    const file = event.dataTransfer.files?.[0];
    if (!file || !file.type.startsWith("image/")) {
      return;
    }

    onDrop(file);
    setShowNoAudiosState(false);
  };

  const handleUseAudios = () => {
    onUseAudios();

    if (!hasAudios) {
      setShowNoAudiosState(true);
      return;
    }

    setShowNoAudiosState(false);
  };

  const handleDelete = () => {
    setShowNoAudiosState(false);
    setIsDragActive(false);
    onDelete();
  };

  const handleEdit = () => {
    setShowNoAudiosState(false);
    setIsDragActive(false);
    onEdit();
  };

  const showDelete = hasCoverArt || showNoAudiosState;
  const squareLabel = hasCoverArt
    ? "Playlist cover art preview"
    : showNoAudiosState
    ? "No media added"
    : "No image selected";

  return (
    <section className={styles.card}>
      <div className={styles.header}>
        <h3 className={styles.title}>Thumbnail Image</h3>
      </div>

      <div className={styles.content}>
        <div
          className={`${styles.square} ${
            hasCoverArt ? styles.squareWithCover : ""
          } ${isDragActive ? styles.squareDragActive : ""}`}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          role="img"
          aria-label={squareLabel}
        >
          {hasCoverArt ? (
            isSingleImagePreview ? (
              <img
                src={coverThumbnails[0]}
                alt="Playlist thumbnail"
                className={styles.singlePreviewImage}
              />
            ) : (
              <PlaylistCoverArt
                thumbnails={coverThumbnails}
                overflowCount={overflowCount}
                className={styles.coverArt}
              />
            )
          ) : (
            <span className={styles.placeholderLabel}>
              {showNoAudiosState ? "No Media Added" : "No Image"}
            </span>
          )}
        </div>

        <input
          ref={inputRef}
          className={styles.hiddenInput}
          type="file"
          accept="image/*"
          onChange={handleInputChange}
        />

        {showDelete ? (
          hasCoverArt ? (
            <div className={styles.actionsRow}>
              <button
                className={styles.editButton}
                onClick={handleEdit}
                type="button"
                aria-label="Edit cover art"
              >
                <span className={styles.editIconWrap}>
                  <img src={EditIcon} alt="" className={styles.editIcon} />
                </span>
                <span className={styles.editLabel}>Edit</span>
              </button>
              <button
                className={styles.deleteButton}
                onClick={handleDelete}
                type="button"
                aria-label="Delete cover art"
              >
                <img
                  src={TrashButtonIcon}
                  alt=""
                  className={styles.deleteIcon}
                />
              </button>
            </div>
          ) : (
            <button
              className={styles.deleteButton}
              onClick={handleDelete}
              type="button"
              aria-label="Delete cover art"
            >
              <img src={TrashButtonIcon} alt="" className={styles.deleteIcon} />
            </button>
          )
        ) : (
          <div className={styles.buttonColumn}>
            <Button
              className={styles.useAudiosButton}
              onClick={handleUseAudios}
              type="button"
            >
              Use Media
            </Button>
            <Button
              className={styles.addExistingButton}
              onClick={handleAddExistingClick}
              type="button"
            >
              Add Existing
            </Button>
          </div>
        )}
      </div>
    </section>
  );
}

PlaylistThumbnailPreview.propTypes = {
  thumbnails: PropTypes.arrayOf(PropTypes.string),
  overflowCount: PropTypes.number,
  hasAudios: PropTypes.bool,
  onAddExisting: PropTypes.func,
  onDrop: PropTypes.func,
  onUseAudios: PropTypes.func,
  onEdit: PropTypes.func,
  onDelete: PropTypes.func,
};

PlaylistThumbnailPreview.defaultProps = {
  thumbnails: [],
  overflowCount: 0,
  hasAudios: false,
  onAddExisting: () => {},
  onDrop: () => {},
  onUseAudios: () => {},
  onEdit: () => {},
  onDelete: () => {},
};

export default PlaylistThumbnailPreview;
